import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Auth axios instance.
 *
 * Migrated to httpOnly cookies (Feb 2026): `withCredentials: true` causes the
 * browser to automatically attach/persist the `civi_session` cookie on every
 * request. No tokens in localStorage = no XSS-readable credentials.
 *
 * `publicApi` carries credentials too so the SSR/admin sessions don't bleed
 * through, but the public endpoints don't read auth.
 */
export const api = axios.create({ baseURL: API, withCredentials: true });
export const publicApi = axios.create({ baseURL: API, withCredentials: true });

/**
 * Conservative 401 handling — DO NOT auto-logout on every 401.
 *
 * Earlier version of this file POSTed `/auth/logout` and redirected to
 * `/admin/login` on ANY 401 with a non-upstream detail. That turned a single
 * 401 (e.g. cookie not yet committed, or a non-auth endpoint that 401s for
 * an unrelated reason) into a guaranteed logout loop.
 *
 * New behavior:
 *   - Only redirect to /admin/login when /auth/me itself returns 401 AND
 *     we're inside the admin section AND not already on the login page.
 *   - Never auto-call /auth/logout from here. The server cookie is already
 *     gone if the request reached this branch — calling logout would
 *     amplify any race condition into a perpetual redirect loop.
 *   - All other 401s propagate to the caller as a normal axios rejection.
 *
 * The AuthContext handles its own bootstrap-time 401 (sets user=false +
 * redirect happens via RequireAuth).
 */
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (
      status === 401 &&
      url.endsWith("/auth/me") &&
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/admin") &&
      window.location.pathname !== "/admin/login" &&
      window.location.pathname !== "/admin/forgot-password" &&
      window.location.pathname !== "/admin/reset-password"
    ) {
      window.location.assign("/admin/login");
    }
    return Promise.reject(err);
  }
);

/**
 * One-shot cleanup of localStorage tokens stored before the cookie migration.
 * Runs at import time so any open browser tab with old keys is purged on next
 * load. Safe — only touches our two keys, no other side effects.
 */
if (typeof window !== "undefined" && window.localStorage) {
  try {
    window.localStorage.removeItem("civi.token");
    window.localStorage.removeItem("civi.email");
  } catch { /* private mode / disabled storage */ }
}
